import { Component, EventEmitter, Input, OnChanges, Output, signal } from '@angular/core'
import { FotoRemotaComponent } from './foto-remota'
import {
  deshacerReemplazo,
  eliminarFoto,
  hayAnteriorPendiente,
  subirOReemplazarFoto,
  type SlotFotoDashboard,
} from '../../infrastructure/storage/reemplazo-fotos-dashboard'
import { ErrorImagenNoSoportada } from '../../infrastructure/util/normalizar-imagen'

/**
 * Un solo componente reusable para "adjuntar/reemplazar/eliminar/deshacer"
 * una foto desde el dashboard — usado en la tarjeta del proveedor, el color
 * de estructura del producto y cada lado de tela (frente/reverso). Evita que
 * cada lugar reimplemente su propia lógica de subida (pedido explícito:
 * un único componente reusable, ver evaluación de riesgos de la feature).
 */
@Component({
  selector: 'app-campo-foto-dashboard',
  standalone: true,
  imports: [FotoRemotaComponent],
  templateUrl: './campo-foto-dashboard.html',
  styleUrl: './campo-foto-dashboard.scss',
})
export class CampoFotoDashboardComponent implements OnChanges {
  @Input({ required: true }) slot!: SlotFotoDashboard
  @Input() url?: string
  @Input() titulo = 'Foto'
  /** Si es false, el componente solo muestra la foto — sin botones de edición (control de permisos vive en quien lo usa). */
  @Input() puedeEditar = false

  @Output() actualizado = new EventEmitter<string | undefined>()

  readonly subiendo = signal(false)
  readonly error = signal<string | null>(null)
  readonly hayAnterior = signal(false)
  readonly mostrarPreguntaQuitar = signal(false)
  readonly mostrarConfirmarEliminar = signal(false)

  private ultimoSlotClave = ''
  private temporizadorError?: ReturnType<typeof setTimeout>

  /** El error se muestra como notificación flotante (no en el flujo normal) para no correr el layout de las tarjetas vecinas — se autodescarta solo. */
  private mostrarErrorTemporal(mensaje: string): void {
    clearTimeout(this.temporizadorError)
    this.error.set(mensaje)
    this.temporizadorError = setTimeout(() => this.error.set(null), 5000)
  }

  ngOnChanges(): void {
    const clave = `${this.slot.tabla}:${this.slot.filaId}:${this.slot.columna}`
    if (clave === this.ultimoSlotClave) return
    this.ultimoSlotClave = clave
    this.refrescarHayAnterior()
  }

  private refrescarHayAnterior(): void {
    if (!this.puedeEditar) return
    hayAnteriorPendiente(this.slot)
      .then((hay) => this.hayAnterior.set(hay))
      .catch((err) => console.warn('[campo-foto-dashboard] no se pudo verificar si hay anterior:', err))
  }

  async alElegirArchivo(evento: Event): Promise<void> {
    const input = evento.target as HTMLInputElement
    const archivo = input.files?.[0]
    input.value = '' // permite volver a elegir el mismo archivo si se cancela y reintenta
    if (!archivo) return

    this.error.set(null)
    this.subiendo.set(true)
    try {
      const urlNueva = await subirOReemplazarFoto(this.slot, archivo)
      this.actualizado.emit(urlNueva)
      this.refrescarHayAnterior()
    } catch (err) {
      this.mostrarErrorTemporal(err instanceof ErrorImagenNoSoportada ? err.message : 'No se pudo subir la foto, intenta de nuevo.')
    } finally {
      this.subiendo.set(false)
    }
  }

  abrirConfirmarEliminar(): void {
    this.mostrarConfirmarEliminar.set(true)
  }

  cerrarConfirmarEliminar(): void {
    this.mostrarConfirmarEliminar.set(false)
  }

  async confirmarEliminar(): Promise<void> {
    this.mostrarConfirmarEliminar.set(false)
    this.error.set(null)
    this.subiendo.set(true)
    try {
      await eliminarFoto(this.slot, this.url ?? null)
      this.actualizado.emit(undefined)
      this.hayAnterior.set(false)
    } catch {
      this.mostrarErrorTemporal('No se pudo eliminar la foto, intenta de nuevo.')
    } finally {
      this.subiendo.set(false)
    }
  }

  abrirPreguntaQuitar(): void {
    this.mostrarPreguntaQuitar.set(true)
  }

  cerrarPreguntaQuitar(): void {
    this.mostrarPreguntaQuitar.set(false)
  }

  async resolverQuitar(accion: 'restaurar' | 'vaciar'): Promise<void> {
    this.mostrarPreguntaQuitar.set(false)
    this.error.set(null)
    this.subiendo.set(true)
    try {
      const urlResultante = await deshacerReemplazo(this.slot, accion)
      this.actualizado.emit(urlResultante ?? undefined)
      this.hayAnterior.set(false)
    } catch {
      this.mostrarErrorTemporal('No se pudo deshacer el reemplazo, intenta de nuevo.')
    } finally {
      this.subiendo.set(false)
    }
  }
}
